import { merge } from 'lodash/fp';
import { insertIntoArray } from 'utils';
import { fieldTypes, selectMultipleType } from '../constants';
import { convertObjToGraphQLSchema, generateObjFromDataPath } from './index';

type Column = {
    key: string;
    dataPath?: string | string[];
    isArrayItem?: boolean;
    fieldType?: string;
    [key: string]: unknown;
};

type Field = {
    key?: string;
    label?: string;
    fieldType?: string;
    isArrayItem?: boolean;
    isSortable?: boolean;
    isFilterable?: boolean;
    listKey?: string;
};

type DataListItem = {
    listKey?: string;
    label?: string;
    id?: string | number;
};

type Rule = {
    key: string;
    value: unknown[];
};

export class ClientSchemaManager {
    static #isArrayType = (isArrayItem?: boolean, dataPath = '', insertValue = ''): string => {
        if (!isArrayItem) return dataPath;

        return insertIntoArray(dataPath.split('.'), 2, insertValue).join('.');
    };

    static #isSelectType = (fieldType?: string): string => {
        if (fieldTypes.includes(fieldType || '')) return '.label';

        return '';
    };

    static getDataPathByKey = (columns: Column[] = []): Record<string, string | string[] | undefined> => (
        columns.reduce((acc, column) => ({
            ...acc,
            [column.key]: typeof column.dataPath === 'string' ? `${
                ClientSchemaManager.#isArrayType(column.isArrayItem, column.dataPath, '[0]')}${ClientSchemaManager.#isSelectType(column.fieldType)}`
                : column.dataPath,
        }), {})
    );

    static getFieldsByColumn = ({
        column = null,
        field = {},
        dataList = [],
    }: {
        column: Column | null;
        field?: Field;
        dataList?: DataListItem[];
    }): Record<string, unknown> => {
        if (!column) return {};

        return (
            {
                onFilter: false,
                title: field?.label ?? '',
                fieldType: field.fieldType,
                isArrayItem: field.isArrayItem,
                sortable: field?.isSortable ?? false,
                filterable: field?.isFilterable ?? false,
                filters: dataList?.filter(({ listKey }) => listKey === field.listKey)
                    .map(({ label, id }) => ({ value: id, label })),
                ...column,
            }
        );
    };

    static generateColumns = ({
        columns = [],
        fields = [],
        dataList = [],
    }: {
        columns?: Column[];
        fields?: Field[];
        dataList?: DataListItem[];
    }): Record<string, unknown>[] => columns.map((column) => (
        this.getFieldsByColumn({
            column,
            dataList,
            field: fields?.find(({ key }) => key === (column.dataPath)),
        })
    ));

    static generateContactFragment = (columns: Column[]): string => {
        const schemaObj = columns.reduce((acc, { dataPath, fieldType }) => (
            ((typeof dataPath === 'string') || !dataPath)
                ? merge(
                    acc,
                    generateObjFromDataPath(
                        dataPath,
                        fieldTypes.includes(fieldType || '')
                            ? { label: '', value: '' }
                            : '',
                    ),
                )
                : dataPath.reduce((multiPathAcc, dataPathItem) => (
                    merge(
                        multiPathAcc,
                        generateObjFromDataPath(
                            dataPathItem,
                            fieldTypes.includes(fieldType || '')
                                ? { label: '', value: '' }
                                : '',
                        ),
                    )
                ), acc)
        ), {});

        return `fragment ContactItems on Contact   ${convertObjToGraphQLSchema(schemaObj)}`;
    };

    static #generateFilterValue = (fieldType = '', value: unknown[] = []): { value?: { in: unknown[] }; some?: { value: { in: unknown[] } } } => {
        if (fieldType === selectMultipleType) {
            return ({ some: { value: { in: value } } });
        }

        return ({ value: { in: value } });
    };

    static generateSortValue = ({
        key = '',
        direction = '',
        columns = [],
    }: {
        key?: string;
        direction?: string;
        columns?: Column[];
    }): Record<string, unknown>[] => {
        const { fieldType, dataPath } = columns.find((item) => key === item.key) || {};

        if (!dataPath) return [];

        if (typeof dataPath !== 'string') return [];

        return [
            generateObjFromDataPath(
                dataPath,
                fieldTypes.includes(fieldType || '') ? { label: direction?.toUpperCase() }
                    : direction?.toUpperCase(),
            ),
        ];
    };

    static generateFilters = (rules: Rule[] = [], columns: Column[] = []): Record<string, unknown> => (
        rules.reduce((acc, item) => {
            const matchedColumn = columns.find(({ key }) => key === item.key);
            const {
                dataPath, isArrayItem, fieldType,
            } = matchedColumn || {};

            if (!matchedColumn || typeof dataPath !== 'string') {
                return acc;
            }

            return (
                merge(
                    acc,
                    generateObjFromDataPath(
                        this.#isArrayType(isArrayItem, dataPath, 'some'),
                        ClientSchemaManager.#generateFilterValue(fieldType, item.value),
                    ),
                )
            );
        }, {})
    );
}
